import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import { 
  Shield, 
  Copy, 
  Check, 
  RefreshCw, 
  Sparkles,
  ChevronDown,
  AlertTriangle,
  Zap,
  Mail,
  Send,
  Star,
  BookOpen,
  Users,
  ArrowRight,
  Clock,
  Brain,
  TrendingUp,
  Lightbulb,
  X,
  Save
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import EmailComposer from "./EmailComposer";
import { useFormAutosave } from "@/hooks/useAutosave";

const TONE_OPTIONS = [
  { value: "firm", label: "Firm", description: "Direct and assertive", color: "text-red-400 bg-red-500/20" },
  { value: "friendly", label: "Friendly", description: "Warm and understanding", color: "text-green-400 bg-green-500/20" },
  { value: "formal", label: "Formal", description: "Professional and structured", color: "text-blue-400 bg-blue-500/20" },
];

// Quick starter prompts to reduce blank-state anxiety
const STARTER_PROMPTS = [
  {
    title: "Client ghosted me",
    situation: "Client hasn't responded to my emails in 2 weeks. They were very engaged before but suddenly went silent. I need to follow up without seeming pushy.",
    tone: "friendly",
    icon: "👻"
  },
  {
    title: "Asking for freebies",
    situation: "Client is asking for additional features/work that wasn't in our original agreement, and they expect it to be included for free.",
    tone: "firm",
    icon: "🎁"
  },
  {
    title: "Ignoring contract terms",
    situation: "Client is requesting changes that go against our signed contract terms. They're asking for unlimited revisions when we agreed on 2 rounds.",
    tone: "formal",
    icon: "📜"
  },
  {
    title: "Late payment",
    situation: "Invoice is 2 weeks overdue. Client acknowledged receipt but hasn't paid. Need to follow up professionally without damaging the relationship.",
    tone: "firm",
    icon: "💰"
  },
  {
    title: "Scope creep",
    situation: "Project scope keeps expanding with 'small' requests that add up. Client says 'it should be quick' but these additions are significant.",
    tone: "friendly",
    icon: "📈"
  },
  {
    title: "Unrealistic deadline",
    situation: "Client wants the project completed in half the time we originally agreed, without adjusting scope or budget.",
    tone: "formal",
    icon: "⏰"
  }
];

// Shimmer loading component for streaming effect
const ResponseShimmer = () => (
  <div className="animate-pulse space-y-3">
    <div className="h-4 bg-slate-700/50 rounded w-3/4"></div>
    <div className="h-4 bg-slate-700/50 rounded w-full"></div>
    <div className="h-4 bg-slate-700/50 rounded w-5/6"></div>
    <div className="h-4 bg-slate-700/50 rounded w-2/3"></div>
    <div className="h-4 bg-slate-700/50 rounded w-full"></div>
    <div className="h-4 bg-slate-700/50 rounded w-4/5"></div>
  </div>
);

export default function ClientFirewall({ onGenerate, usage }) {
  const location = useLocation();
  const navigate = useNavigate();
  const responseRef = useRef(null);
  
  // Auto-save draft functionality
  const { 
    values: draftValues, 
    setField: setDraftField, 
    setValues: setDraftValues,
    clearDraft, 
    hasDraft 
  } = useFormAutosave('client_firewall', {
    situation: "",
    tone: "friendly",
    projectId: "",
    clientId: "",
    context: { project_type: "", payment_terms: "", last_contact_date: "", notes: "" }
  });
  
  // Use draft values as initial state
  const [situation, setSituation] = useState(draftValues.situation || "");
  const [tone, setTone] = useState(draftValues.tone || "friendly");
  const [projectId, setProjectId] = useState(draftValues.projectId || "");
  const [clientId, setClientId] = useState(draftValues.clientId || "");
  const [context, setContext] = useState(draftValues.context || {
    project_type: "",
    payment_terms: "",
    last_contact_date: "",
    notes: ""
  });
  
  // Sync changes to auto-save
  useEffect(() => {
    setDraftValues({ situation, tone, projectId, clientId, context });
  }, [situation, tone, projectId, clientId, context]);
  
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState("");
  const [showAlternative, setShowAlternative] = useState(false);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailContent, setEmailContent] = useState("");
  const [streamingText, setStreamingText] = useState("");
  const [smartSuggestions, setSmartSuggestions] = useState(null);
  const [showSmartSuggestions, setShowSmartSuggestions] = useState(true);

  // Handle navigation state (from Clients page Quick Actions or Message History reuse)
  useEffect(() => {
    if (location.state?.clientId) {
      setClientId(location.state.clientId);
      // Clear navigation state
      navigate(location.pathname, { replace: true });
    }
    
    // Handle reuse from Message History
    if (location.state?.reuseMessage) {
      const { situation: reuseSituation, tone: reuseTone, context: reuseContext } = location.state.reuseMessage;
      if (reuseSituation) setSituation(reuseSituation);
      if (reuseTone) setTone(reuseTone);
      if (reuseContext) setContext(prev => ({ ...prev, ...reuseContext }));
      // Clear navigation state
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, templatesRes, clientsRes, suggestionsRes] = await Promise.all([
          api.get("/projects"),
          api.get("/outreach/templates"),
          api.get("/clients"),
          api.get("/messages/smart-suggestions").catch(() => ({ data: null }))
        ]);
        setProjects(projectsRes.data);
        setTemplates(templatesRes.data?.templates || []);
        setClients(clientsRes.data);
        if (suggestionsRes.data) {
          setSmartSuggestions(suggestionsRes.data);
          // Auto-set recommended tone if user has history
          if (suggestionsRes.data.has_history && suggestionsRes.data.suggestions?.recommended_tone) {
            setTone(suggestionsRes.data.suggestions.recommended_tone);
          }
        }
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  // Auto-fill context when client or project is selected
  useEffect(() => {
    if (clientId) {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setContext(prev => ({
          ...prev,
          notes: client.notes || prev.notes
        }));
        // Auto-select first project for this client
        const clientProjects = projects.filter(p => p.client_id === clientId);
        if (clientProjects.length > 0 && !projectId) {
          setProjectId(clientProjects[0].id);
        }
      }
    }
  }, [clientId, clients, projects, projectId]);

  useEffect(() => {
    if (projectId && projectId !== "none") {
      const project = projects.find(p => p.id === projectId);
      if (project) {
        // Auto-fill project type from tags
        const projectType = project.tags?.join(", ") || "";
        setContext(prev => ({
          ...prev,
          project_type: projectType || prev.project_type
        }));
        // Auto-select the client for this project
        if (project.client_id && !clientId) {
          setClientId(project.client_id);
        }
      }
    }
  }, [projectId, projects, clientId]);

  // Simulate streaming effect for response
  const simulateStreaming = (text) => {
    setStreamingText("");
    const words = text.split(" ");
    let currentIndex = 0;
    
    const interval = setInterval(() => {
      if (currentIndex < words.length) {
        setStreamingText(prev => prev + (currentIndex > 0 ? " " : "") + words[currentIndex]);
        currentIndex++;
      } else {
        clearInterval(interval);
        setResponse(prev => prev); // Trigger final state
      }
    }, 20); // Fast streaming effect
    
    return () => clearInterval(interval);
  };

  const handleGenerate = async () => {
    if (situation.length < 8) {
      toast.error("Please describe the situation in more detail (at least 8 characters)");
      return;
    }

    setLoading(true);
    setResponse(null);
    setStreamingText("");
    setShowAlternative(false);

    try {
      const res = await api.post("/generate-reply", {
        situation,
        tone,
        context,
        project_id: projectId && projectId !== "none" ? projectId : undefined
      });

      // Start streaming effect
      simulateStreaming(res.data.primary);
      setResponse(res.data);
      
      // Scroll to response
      setTimeout(() => {
        responseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      
      onGenerate?.();
    } catch (e) {
      console.error("Generate error:", e);
      if (e.response?.status === 402) {
        toast.error("Monthly limit reached. Please upgrade your plan.");
      } else {
        toast.error(e.response?.data?.detail || "Failed to generate reply");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setCopiedMessage("Copied! Drop this into your email or DM.");
      setTimeout(() => {
        setCopied(false);
        setCopiedMessage("");
      }, 3000);
    } catch (error) {
      console.warn("Clipboard API failed, using fallback:", error);
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setCopiedMessage("Copied! Drop this into your email or DM.");
        setTimeout(() => {
          setCopied(false);
          setCopiedMessage("");
        }, 3000);
      } catch (fallbackError) {
        toast.error("Unable to copy to clipboard. Please copy manually.");
      }
      document.body.removeChild(textArea);
    }
  };

  const handleOpenInEmail = (text) => {
    const subjectMatch = text.match(/Subject:\s*(.+?)(?:\n|$)/i);
    const subject = subjectMatch ? subjectMatch[1].trim() : "Client Reply";
    let body = text.replace(/Subject:\s*.+?\n/i, '').trim();
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    toast.success("Opening email client...");
  };

  const handleTemplateSelect = (template) => {
    setSituation(template.situation);
    setTone(template.suggested_tone);
    setShowTemplates(false);
    toast.success(`Template loaded: ${template.title}`);
  };

  const handleStarterSelect = (starter) => {
    setSituation(starter.situation);
    setTone(starter.tone);
    toast.success(`"${starter.title}" loaded - edit as needed!`);
  };

  const usagePercent = usage ? (usage.gen_count_month / usage.gen_cap_month) * 100 : 0;
  const isLimitReached = usage && usage.remaining <= 0;

  // Get filtered projects based on selected client
  const filteredProjects = clientId 
    ? projects.filter(p => p.client_id === clientId)
    : projects;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="client-firewall">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Client Firewall</h1>
        <p className="text-slate-400 max-w-md mx-auto">
          Generate calm, professional replies to tricky client situations
        </p>
      </div>

      {/* Usage Warning */}
      {usage && usage.remaining <= 5 && (
        <div className={`p-4 rounded-xl border ${
          isLimitReached 
            ? 'bg-red-500/10 border-red-500/30' 
            : 'bg-yellow-500/10 border-yellow-500/30'
        }`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-5 h-5 ${isLimitReached ? 'text-red-400' : 'text-yellow-400'}`} />
            <div>
              <p className={`font-medium ${isLimitReached ? 'text-red-400' : 'text-yellow-400'}`}>
                {isLimitReached ? 'Monthly limit reached' : `${usage.remaining} generations remaining`}
              </p>
              <p className="text-sm text-slate-400">
                {isLimitReached ? 'Upgrade to continue generating replies' : 'Consider upgrading for more generations'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Draft Recovery Banner */}
      {hasDraft && situation && (
        <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/50 border border-slate-700/50">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Save className="w-4 h-4 text-green-400" />
            <span>Draft auto-saved</span>
          </div>
          <button
            onClick={() => {
              setSituation("");
              setTone("friendly");
              setProjectId("");
              setClientId("");
              setContext({ project_type: "", payment_terms: "", last_contact_date: "", notes: "" });
              clearDraft();
              toast.success("Draft cleared");
            }}
            className="text-xs text-slate-500 hover:text-red-400 transition-colors"
          >
            Clear draft
          </button>
        </div>
      )}

      {/* Smart Suggestions - Personalized based on history */}
      {smartSuggestions?.has_history && showSmartSuggestions && !situation && !response && (
        <Card className="bg-gradient-to-br from-indigo-900/30 to-purple-900/20 border-indigo-500/30" data-testid="smart-suggestions">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Brain className="w-5 h-5 text-indigo-400" />
                Smart Suggestions
                <Badge className="bg-indigo-500/20 text-indigo-300 text-xs">AI</Badge>
              </CardTitle>
              <button
                onClick={() => setShowSmartSuggestions(false)}
                className="p-1 text-slate-500 hover:text-slate-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <CardDescription className="text-slate-400">
              Personalized based on your {smartSuggestions.total_messages} previous messages
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Insights */}
            {smartSuggestions.suggestions?.insights?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {smartSuggestions.suggestions.insights.map((insight, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-slate-300 bg-slate-800/50 px-3 py-1.5 rounded-full">
                    <Lightbulb className="w-3 h-3 text-yellow-400" />
                    {insight}
                  </div>
                ))}
              </div>
            )}

            {/* Recommended Tone */}
            <div className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-xl">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span className="text-sm text-slate-400">Your most effective tone:</span>
              </div>
              <Badge className={`${
                smartSuggestions.suggestions?.recommended_tone === 'firm' 
                  ? 'bg-red-500/20 text-red-400' 
                  : smartSuggestions.suggestions?.recommended_tone === 'friendly'
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-blue-500/20 text-blue-400'
              }`}>
                {smartSuggestions.suggestions?.recommended_tone}
              </Badge>
              <div className="flex-1 flex gap-2 justify-end">
                {Object.entries(smartSuggestions.suggestions?.tone_percentages || {}).map(([t, pct]) => (
                  <span key={t} className="text-xs text-slate-500">
                    {t}: {pct}%
                  </span>
                ))}
              </div>
            </div>

            {/* Personalized Templates */}
            {smartSuggestions.suggestions?.personalized_templates?.length > 0 && (
              <div>
                <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                  <Zap className="w-3 h-3 text-yellow-400" />
                  Suggested templates based on your patterns
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {smartSuggestions.suggestions.personalized_templates.map((template, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleStarterSelect(template)}
                      className="text-left p-4 bg-slate-800/70 hover:bg-slate-700/70 rounded-xl transition-all border border-slate-600/30 hover:border-indigo-500/50 group"
                      data-testid={`smart-template-${idx}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">{template.icon}</span>
                        <span className="font-medium text-slate-200 text-sm group-hover:text-indigo-300 transition-colors">
                          {template.title}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 line-clamp-2 mb-2">{template.situation.slice(0, 60)}...</p>
                      <p className="text-xs text-indigo-400/70">{template.reason}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Starter Prompts - Always visible when no situation */}
      {!situation && !response && (
        <Card className="bg-slate-800/50 border-slate-700/50 border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-400" />
              Quick Start Templates
            </CardTitle>
            <CardDescription className="text-slate-400">
              Click a common scenario to get started, then customize as needed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {STARTER_PROMPTS.map((starter, index) => (
                <button
                  key={index}
                  onClick={() => handleStarterSelect(starter)}
                  className="text-left p-4 bg-slate-900/50 hover:bg-slate-700/50 rounded-xl transition-all border border-slate-600/30 hover:border-indigo-500/50 group"
                  data-testid={`starter-${index}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{starter.icon}</span>
                    <span className="font-medium text-slate-200 text-sm group-hover:text-indigo-300 transition-colors">
                      {starter.title}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 line-clamp-2">{starter.situation.slice(0, 80)}...</p>
                  <div className="mt-2 flex items-center gap-1 text-xs text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowRight className="w-3 h-3" />
                    Use this
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Form */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Describe the Situation</span>
            <button
              onClick={() => setShowTemplates(!showTemplates)}
              className="text-sm font-normal text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
              data-testid="templates-toggle"
            >
              <Zap className="w-4 h-4" />
              More Templates
              <ChevronDown className={`w-4 h-4 transition-transform ${showTemplates ? 'rotate-180' : ''}`} />
            </button>
          </CardTitle>
          <CardDescription className="text-slate-400">
            What&apos;s happening with your client? Be specific for better results.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Templates Dropdown */}
          {showTemplates && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-4 bg-slate-700/30 rounded-xl">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template)}
                  className="text-left p-3 bg-slate-800/50 hover:bg-slate-700/50 rounded-lg transition-colors border border-slate-600/30 hover:border-indigo-500/30"
                  data-testid={`template-${template.id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-slate-200 text-sm">{template.title}</span>
                    <Badge className={`text-xs ${
                      template.suggested_tone === 'firm' 
                        ? 'bg-red-500/20 text-red-400' 
                        : template.suggested_tone === 'friendly'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {template.suggested_tone}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-2">{template.situation}</p>
                </button>
              ))}
            </div>
          )}

          {/* Situation Input */}
          <div>
            <Textarea
              value={situation}
              onChange={(e) => setSituation(e.target.value)}
              placeholder="e.g., Client hasn't paid after 2 weeks past the invoice due date. They were responsive before but now not replying to my emails."
              className="min-h-[120px] bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-indigo-500"
              data-testid="situation-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              {situation.length} characters (minimum 8 required)
            </p>
          </div>

          {/* Tone Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">Response Tone</label>
            <div className="grid grid-cols-3 gap-3">
              {TONE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTone(option.value)}
                  className={`p-4 rounded-xl border transition-all ${
                    tone === option.value
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-slate-600 bg-slate-900/30 hover:border-slate-500'
                  }`}
                  data-testid={`tone-${option.value}`}
                >
                  <div className={`text-sm font-medium ${tone === option.value ? 'text-indigo-400' : 'text-slate-300'}`}>
                    {option.label}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Client & Project Quick Select */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-slate-300 flex items-center gap-2">
              <Users className="w-4 h-4 text-indigo-400" />
              Quick Fill from Client/Project
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client Selection */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Select Client</label>
                <Select value={clientId} onValueChange={setClientId}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue placeholder="Choose a client..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="none" className="text-slate-300">No client</SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="text-slate-300">
                        {c.name} {c.company && `(${c.company})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Project Selection */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Link to Project</label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-600 text-white">
                    <SelectValue placeholder="Select project..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="none" className="text-slate-300">No project</SelectItem>
                    {filteredProjects.map((p) => (
                      <SelectItem key={p.id} value={p.id} className="text-slate-300">
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Additional Context */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-slate-300">Additional Context (Optional)</label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Project Type */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Project Type</label>
                <input
                  type="text"
                  value={context.project_type}
                  onChange={(e) => setContext({...context, project_type: e.target.value})}
                  placeholder="e.g., Web Design, Consulting"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
                />
              </div>

              {/* Payment Terms */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Payment Terms</label>
                <input
                  type="text"
                  value={context.payment_terms}
                  onChange={(e) => setContext({...context, payment_terms: e.target.value})}
                  placeholder="e.g., Net 30, 50% upfront"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
                />
              </div>

              {/* Last Contact */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Last Contact Date</label>
                <input
                  type="text"
                  value={context.last_contact_date}
                  onChange={(e) => setContext({...context, last_contact_date: e.target.value})}
                  placeholder="e.g., 2 weeks ago, July 15"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
                />
              </div>

              {/* Client Notes */}
              <div>
                <label className="block text-xs text-slate-400 mb-1">Client Notes</label>
                <input
                  type="text"
                  value={context.notes}
                  onChange={(e) => setContext({...context, notes: e.target.value})}
                  placeholder="e.g., Prefers formal communication"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={loading || isLimitReached || situation.length < 8}
            className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:from-slate-600 disabled:to-slate-700 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            data-testid="generate-btn"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Generate Professional Reply
              </>
            )}
          </button>

          {/* Usage Info */}
          {usage && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">
                {usage.remaining} generations remaining this month
              </span>
              <div className="w-32">
                <Progress value={usagePercent} className="h-1.5" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live Response Preview Area */}
      {(loading || response) && (
        <Card ref={responseRef} className="bg-slate-800/50 border-slate-700/50 border-indigo-500/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                {loading ? (
                  <>
                    <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
                    Generating Reply...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 text-green-400" />
                    Generated Reply
                  </>
                )}
              </CardTitle>
              {response && (
                <div className="flex items-center gap-2">
                  <Badge className={`${
                    tone === 'firm' 
                      ? 'bg-red-500/20 text-red-400' 
                      : tone === 'friendly'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {tone}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    {response.elapsed_ms}ms
                  </span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Live Preview / Shimmer */}
            <div className="relative">
              <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-600 min-h-[200px]">
                {loading && !streamingText ? (
                  <ResponseShimmer />
                ) : (
                  <pre className="whitespace-pre-wrap text-slate-200 font-sans text-sm leading-relaxed" data-testid="response-primary">
                    {streamingText || response?.primary}
                    {loading && <span className="animate-pulse">▊</span>}
                  </pre>
                )}
              </div>
              
              {/* Copy & Send Actions - visible when response is ready */}
              {response && !loading && (
                <div className="absolute top-3 right-3 flex gap-1">
                  <button
                    onClick={() => handleOpenInEmail(response.primary)}
                    className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                    title="Open in email client"
                    data-testid="email-primary-btn"
                  >
                    <Mail className="w-4 h-4 text-indigo-400" />
                  </button>
                  <button
                    onClick={() => handleCopy(response.primary)}
                    className={`p-2 rounded-lg transition-all flex items-center gap-2 ${
                      copied 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-slate-700 hover:bg-slate-600 text-slate-400'
                    }`}
                    data-testid="copy-primary-btn"
                  >
                    {copied ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Copy Success Message */}
            {copiedMessage && (
              <div className="flex items-center gap-2 text-sm text-green-400 bg-green-500/10 px-4 py-2 rounded-lg animate-in fade-in slide-in-from-top-2">
                <Check className="w-4 h-4" />
                {copiedMessage}
              </div>
            )}

            {/* Alternative Toggle */}
            {response && response.alternative && !loading && (
              <>
                <button
                  onClick={() => setShowAlternative(!showAlternative)}
                  className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
                  data-testid="show-alternative-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  {showAlternative ? 'Hide' : 'Show'} alternative version
                </button>

                {showAlternative && (
                  <div className="relative">
                    <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-600">
                      <pre className="whitespace-pre-wrap text-slate-200 font-sans text-sm leading-relaxed" data-testid="response-alternative">
                        {response.alternative}
                      </pre>
                    </div>
                    <div className="absolute top-3 right-3 flex gap-1">
                      <button
                        onClick={() => handleOpenInEmail(response.alternative)}
                        className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                        title="Open in email client"
                      >
                        <Mail className="w-4 h-4 text-indigo-400" />
                      </button>
                      <button
                        onClick={() => handleCopy(response.alternative)}
                        className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                      >
                        <Copy className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Actions */}
            {response && !loading && (
              <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/message-history')}
                    className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1 transition-colors"
                  >
                    <Clock className="w-3 h-3" />
                    View all in History
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setResponse(null);
                      setSituation("");
                      setStreamingText("");
                    }}
                    className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200 transition-colors"
                  >
                    Clear & Start Over
                  </button>
                  <button
                    onClick={() => {
                      setEmailContent(response.primary);
                      setShowEmailComposer(true);
                    }}
                    className="px-4 py-2 text-sm bg-green-500/20 text-green-400 hover:bg-green-500/30 rounded-lg transition-colors flex items-center gap-1"
                    data-testid="compose-email-btn"
                  >
                    <Send className="w-4 h-4" />
                    Compose Email
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={loading}
                    className="px-4 py-2 text-sm bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 rounded-lg transition-colors flex items-center gap-1"
                    data-testid="regenerate-btn"
                  >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Regenerate
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Composer Modal */}
      <EmailComposer
        isOpen={showEmailComposer}
        onClose={() => setShowEmailComposer(false)}
        initialContent={emailContent}
        clients={clients}
      />
    </div>
  );
}
